import { client } from '@/services/api/axios.ts';
import toast from 'react-hot-toast';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { TPlant } from '@/features/plants/schemas/plants.schema.ts';
import { uploadImages } from '@/features/plants/utils/upload-images.ts';
import { plantKeys } from '@/features/plants/plant-query-keys.ts';

type UpdatePlantResponse = {
  body: TPlant;
};

type UseUpdatePlantOptions = {
  onSuccess?: (data: TPlant) => void;
  onError?: (error: Error) => void;
};

export const useUpdatePlant = ({ onSuccess, onError }: UseUpdatePlantOptions = {}) => {
  const queryClient = useQueryClient();

  const updatePlantWithImages = async (plant: TPlant): Promise<TPlant> => {
    if (!plant.basicDetails.name || !plant.plantId) {
      throw new Error('Missing required plant information');
    }

    const { data } = await client.patch<UpdatePlantResponse>(`/plants/${plant.plantId}`, {
      ...plant,
      siteInformation: {
        ...plant.siteInformation,
        image:
          typeof plant?.siteInformation?.image === 'string' ? plant.siteInformation.image : '',
      },
    });

    if (!data?.body?.plantId) {
      throw new Error('No plant ID returned from server.');
    }

    await uploadImages(plant.siteInformation.image, data.body.plantId);

    return data.body;
  };

  return useMutation({
    mutationFn: (plant: TPlant) =>
      toast.promise(updatePlantWithImages(plant), {
        loading: 'Updating plant...',
        success: 'Successfully updated the plant.',
        error: (err: Error) => err.message || 'Failed to update plant.',
      }),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: plantKeys.all });
      queryClient.invalidateQueries({ queryKey: ['bess'] });

      onSuccess?.(data);
    },
    onError: (error: Error) => {
      onError?.(error);
    },
  });
};
